import { HORIZONTAL_SIDES, VERTICAL_SIDES } from 'ContentUtils/constants/Border';

// Requires verticalSide and/or horizontalSide

/**
 * Returns true if `alpha` is a valid decimal alpha value in the range [0, 1].
 */
export const isValidAlphaDec = alpha => {
  if (isNaN(alpha) || typeof alpha !== 'number' || alpha < 0 || alpha > 1) {
    return false;
  }
  return true;
};

/**
 * Returns true if `alpha` is a valid integer alpha value in the range [0, 100],
 * or null (null is treated as "no alpha specified" and is considered valid).
 */
export const isValidAlphaInt = alpha => {
  const isNotANumericAlpha = isNaN(alpha) || typeof alpha !== 'number';
  const isValidAlphaRange = alpha >= 0 && alpha <= 100;
  return alpha == null || !isNotANumericAlpha && isValidAlphaRange;
};

/**
 * Returns true if `colorChannels` is a valid RGB object with r, g, b each in [0, 255].
 */
export const isValidRGB = colorChannels => {
  const rgb = ['r', 'g', 'b'];
  // The RGB values all have the same valid range, check if any one of them is bad
  const hasValidRGBValues = rgb.every(channel => {
    if (isNaN(colorChannels[channel]) || typeof colorChannels[channel] !== 'number' || colorChannels[channel] < 0 || colorChannels[channel] > 255) {
      return false;
    }
    return true;
  });
  // If there are no bad RGB values and we made is this far, than this is a valid RGBA object
  if (hasValidRGBValues) {
    return true;
  } else {
    return false;
  }
};
const HSB_MAX_VALUES = {
  h: 360,
  s: 100,
  b: 100
};

/**
 * Returns true if `hsbObject` is a valid HSB color with h in [0, 360], s in [0, 100], b in [0, 100].
 */
export const isValidHsbObject = hsbObject => {
  const hasValidHsbValues = Object.keys(HSB_MAX_VALUES).every(key => {
    const actualValue = hsbObject[key];
    const maxValue = HSB_MAX_VALUES[key];
    return !isNaN(actualValue) && typeof actualValue === 'number' && actualValue >= 0 && actualValue <= maxValue;
  });
  return hasValidHsbValues;
};

/**
 * Returns true if `colorChannels` is a valid RGBA object.
 */
export const isValidRGBA = colorChannels => {
  return isValidAlphaDec(colorChannels.a) && isValidRGB(colorChannels);
};

/**
 * Returns true if `colorString` is a valid CSS `rgb(r, g, b)` string.
 */
export const isValidRgbString = colorString => {
  return /^rgb\((\d{1,3}%?),\s*(\d{1,3}%?),\s*(\d{1,3}%?)\)$/i.test(colorString);
};

/**
 * Returns true if `colorString` is a valid CSS `rgba(r, g, b, a)` string.
 */
export const isValidRgbaString = colorString => {
  return /^rgba\((\d{1,3}%?),\s*(\d{1,3}%?),\s*(\d{1,3}%?),\s*(\d*(?:\.\d+)?)\)$/i.test(colorString);
};

/**
 * Returns true if `color` is a string of 0–6 hex digits, optionally prefixed with `#`.
 * Accepts partial or incomplete hex codes — use `isValidHex` for full validation.
 */
export const isHexString = color => typeof color === 'string' && Boolean(color) && /^#?[0-9A-Fa-f]{0,6}$/.test(color);

/**
 * Returns true if `hex` is null or a fully valid 3- or 6-digit hex color string prefixed with `#`.
 */
export const isValidHex = hex => {
  return hex == null || /^#([0-9A-F]{6}|[0-9A-F]{3})$/i.test(hex);
};

/**
 * Returns true if `sideOrCornerObject` is a valid gradient direction.
 * Requires at least one of `verticalSide` or `horizontalSide`, and each must be a
 * recognized value from the `VERTICAL_SIDES` / `HORIZONTAL_SIDES` constants.
 */
export const isValidGradientDirection = sideOrCornerObject => {
  const {
    verticalSide,
    horizontalSide
  } = sideOrCornerObject;
  if (!verticalSide && !horizontalSide) {
    console.warn('Gradient sideOrCorner key must specify the keys "verticalSide" and/or "horizonalSide"');
    return false;
  }
  if (verticalSide && verticalSide !== VERTICAL_SIDES.TOP && verticalSide !== VERTICAL_SIDES.BOTTOM) {
    return false;
  }
  if (horizontalSide && horizontalSide !== HORIZONTAL_SIDES.LEFT && horizontalSide !== HORIZONTAL_SIDES.RIGHT) {
    return false;
  }
  return true;
};

/**
 * Converts a single 0–255 color channel integer to a 2-character lowercase hex string.
 * Zero-pads single-digit results (e.g., `5` → `"05"`).
 * @param color - Integer in [0, 255]
 * @returns 2-character hex string
 */
export const convertColorBitToHex = color => {
  let hex = Number(color).toString(16);
  if (hex.length < 2) hex = `0${hex}`;
  return hex;
};

/**
 * Converts an `RGBType` object to a 6-digit hex string (e.g., `"#ff0000"`).
 * Returns null if any channel is null or the result is not a valid hex.
 * @param rgb - Object with r, g, b channels in [0, 255]
 * @returns Hex string like `"#rrggbb"`, or null on invalid input
 */
export const convertRgbToHex = ({
  r,
  g,
  b
}) => {
  if (r == null || g == null || b == null) return null;
  const red = convertColorBitToHex(r);
  const green = convertColorBitToHex(g);
  const blue = convertColorBitToHex(b);
  const hex = `#${red}${green}${blue}`;
  return isValidHex(hex) ? hex : null;
};
const DEFAULT_ALPHA_INT = 100;

/**
 * Converts a decimal alpha value (0–1) to an integer alpha value (0–100).
 * Returns 100 if the input is not a valid decimal alpha.
 * @param alphaDec - Decimal alpha in [0, 1], or null
 * @returns Integer alpha in [0, 100]
 */
export const convertAlphaDecToInt = alphaDec => {
  const alphaInt = typeof alphaDec === 'number' ? alphaDec * DEFAULT_ALPHA_INT : DEFAULT_ALPHA_INT;
  return isValidAlphaInt(alphaInt) ? alphaInt : DEFAULT_ALPHA_INT;
};
const DEFAULT_ALPHA_DEC = 1.0;

/**
 * Converts an integer alpha value (0–100) to a decimal alpha value (0–1).
 * Returns 1.0 if the input is null or not a valid number.
 * @param alphaInt - Integer alpha in [0, 100], or null
 * @returns Decimal alpha in [0, 1]
 */
export const convertAlphaIntToDec = alphaInt => {
  if (alphaInt === null || typeof alphaInt !== 'number') {
    return DEFAULT_ALPHA_DEC;
  }
  const alphaDec = alphaInt / DEFAULT_ALPHA_INT;
  return isValidAlphaDec(alphaDec) ? alphaDec : DEFAULT_ALPHA_DEC;
};

/**
 * Converts an `RGBAType` object to `{ hex, alpha }` where hex is a 6-digit hex string
 * and alpha is an integer in [0, 100].
 * @param rgba - RGBA object with decimal alpha
 * @returns `{ hex: string | null, alpha: number }`
 */
export const convertRgbaToHexAndAlphaInt = ({
  r,
  g,
  b,
  a = 1.0
}) => {
  return {
    hex: convertRgbToHex({
      r,
      g,
      b
    }),
    alpha: convertAlphaDecToInt(a)
  };
};

/** Rounds an HSB value to two decimal places. */
const roundHsbValue = num => Math.round(num * 100) / 100;

/**
 * Converts an `RGBType` object to an `HSBType` object (Hue 0–360, Saturation 0–100, Brightness 0–100).
 * Returns null if the input is invalid.
 * Algorithm from https://www.30secondsofcode.org/js/s/rgb-hex-hsl-hsb-color-format-conversion/#rgb-to-hsb
 * @param rgbObject - RGB object with channels in [0, 255]
 * @returns HSB object, or null if input is invalid
 */
export const convertRgbToHsb = rgbObject => {
  if (!rgbObject || !isValidRGB(rgbObject)) {
    return null;
  }
  let {
    r,
    g,
    b
  } = rgbObject;
  r = r / 255;
  g = g / 255;
  b = b / 255;
  const maxValue = Math.max(r, g, b);
  const minMaxDiff = maxValue - Math.min(r, g, b);
  const h = minMaxDiff === 0 ? 0 : minMaxDiff && maxValue === r ? (g - b) / minMaxDiff : maxValue === g ? 2 + (b - r) / minMaxDiff : 4 + (r - g) / minMaxDiff;
  return {
    h: roundHsbValue(60 * (h < 0 ? h + 6 : h)),
    s: roundHsbValue(maxValue && minMaxDiff / maxValue * 100),
    b: roundHsbValue(maxValue * 100)
  };
};

/**
 * Converts an `HSBType` object (Hue 0–360, Saturation 0–100, Brightness 0–100) to an `RGBType` object.
 * Returns null if the input is invalid (e.g., any channel out of range).
 * Algorithm from https://www.30secondsofcode.org/js/s/rgb-hex-hsl-hsb-color-format-conversion/#hsb-to-rgb
 * @param hsbObject - HSB object with h in [0, 360], s and b in [0, 100]
 * @returns RGB object with channels in [0, 255], or null if input is invalid
 */
export const convertHsbToRgb = hsbObject => {
  if (!hsbObject || !isValidHsbObject(hsbObject)) {
    return null;
  }
  const {
    h
  } = hsbObject;
  let {
    s,
    b
  } = hsbObject;
  s = s / 100;
  b = b / 100;
  const convertHue = n => (n + h / 60) % 6;
  const convertToRgbValue = n => 255 * (b * (1 - s * Math.max(0, Math.min(convertHue(n), 4 - convertHue(n), 1))));
  return {
    r: Math.round(convertToRgbValue(5)),
    g: Math.round(convertToRgbValue(3)),
    b: Math.round(convertToRgbValue(1))
  };
};
const EMPTY_RGB_OBJECT = {
  r: null,
  b: null,
  g: null
};

/** Extracts the numeric channel values from a CSS `rgb()` or `rgba()` string. */
const getRbgNumberArray = rgbString => {
  var _rgbString$split$1$sp, _rgbString$split$;
  const rgbStringArray = (_rgbString$split$1$sp = (_rgbString$split$ = rgbString.split('(')[1]) === null || _rgbString$split$ === void 0 || (_rgbString$split$ = _rgbString$split$.split(')')[0]) === null || _rgbString$split$ === void 0 ? void 0 : _rgbString$split$.split(',')) !== null && _rgbString$split$1$sp !== void 0 ? _rgbString$split$1$sp : [];
  return rgbStringArray.map(stringNum => parseFloat(stringNum));
};

/**
 * Parses a CSS `rgb(r, g, b)` string into an `RGBType` object.
 * @param rgbString - CSS rgb string, e.g. `"rgb(255, 0, 0)"`
 * @returns RGB object, or `{ r: null, g: null, b: null }` if invalid
 */
export const convertRgbStringToRgbObject = rgbString => {
  var _rgbNumberArray$, _rgbNumberArray$2, _rgbNumberArray$3;
  if (typeof rgbString !== 'string') {
    return EMPTY_RGB_OBJECT;
  }
  const rgbNumberArray = getRbgNumberArray(rgbString);
  const rgbObject = {
    r: (_rgbNumberArray$ = rgbNumberArray[0]) !== null && _rgbNumberArray$ !== void 0 ? _rgbNumberArray$ : null,
    g: (_rgbNumberArray$2 = rgbNumberArray[1]) !== null && _rgbNumberArray$2 !== void 0 ? _rgbNumberArray$2 : null,
    b: (_rgbNumberArray$3 = rgbNumberArray[2]) !== null && _rgbNumberArray$3 !== void 0 ? _rgbNumberArray$3 : null
  };
  return isValidRGB(rgbObject) ? rgbObject : EMPTY_RGB_OBJECT;
};

/**
 * Parses a CSS `rgba(r, g, b, a)` string into an `RGBAType` object.
 * @param rgbaString - CSS rgba string, e.g. `"rgba(255, 0, 0, 0.5)"`
 * @returns RGBA object, or `{ r: null, g: null, b: null, a: null }` if invalid
 */
export const convertRgbaStringToRgbaObject = rgbaString => {
  var _rgbaNumberArray$, _rgbaNumberArray$2, _rgbaNumberArray$3;
  if (typeof rgbaString !== 'string') {
    return Object.assign({}, EMPTY_RGB_OBJECT, {
      a: null
    });
  }
  const rgbaNumberArray = getRbgNumberArray(rgbaString);
  const rgbaObject = {
    r: (_rgbaNumberArray$ = rgbaNumberArray[0]) !== null && _rgbaNumberArray$ !== void 0 ? _rgbaNumberArray$ : null,
    g: (_rgbaNumberArray$2 = rgbaNumberArray[1]) !== null && _rgbaNumberArray$2 !== void 0 ? _rgbaNumberArray$2 : null,
    b: (_rgbaNumberArray$3 = rgbaNumberArray[2]) !== null && _rgbaNumberArray$3 !== void 0 ? _rgbaNumberArray$3 : null,
    a: typeof rgbaNumberArray[3] === 'number' ? rgbaNumberArray[3] : 1
  };
  return isValidRGBA(rgbaObject) ? rgbaObject : Object.assign({}, EMPTY_RGB_OBJECT, {
    a: null
  });
};

/**
 * Converts a hex color string and an optional decimal alpha (0–1, default 1) to an `RGBAType` object.
 * Accepts both 3- and 6-digit hex strings prefixed with `#`.
 * @param hex - Hex color string, e.g. `"#ff0000"` or `"#f00"`
 * @param alpha - Decimal alpha in [0, 1], defaults to 1
 * @returns RGBA object, or `{ r: null, g: null, b: null, a: null }` if hex is invalid
 */
export const convertHexAndAlphaToRgbaObject = (hex, alpha = 1) => {
  if (!isValidHex(hex) || !hex) return Object.assign({}, EMPTY_RGB_OBJECT, {
    a: null
  });
  hex = hex.replace('#', '');

  // 3 characters is a valid hex, double it to 6 characters for the sake of
  // computed rgba values if needed. Changes "abc" to "aabbcc"
  if (hex.length === 3) {
    const sixDigitHex = hex.split('').reduce((accumulator, currentValue) => {
      return accumulator + currentValue + currentValue;
    }, '');
    hex = sixDigitHex;
  }
  const rgba = {
    r: parseInt(hex.substring(0, 2), 16),
    g: parseInt(hex.substring(2, 4), 16),
    b: parseInt(hex.substring(4, 6), 16),
    a: alpha
  };
  return isValidRGBA(rgba) ? rgba : Object.assign({}, EMPTY_RGB_OBJECT, {
    a: null
  });
};

/**
 * Converts a hex color string and an integer alpha (0–100, default 100) to an `RGBAType` object.
 * Normalizes the integer alpha to decimal before calling `convertHexAndAlphaToRgbaObject`.
 * @param hex - Hex color string
 * @param opacity - Integer alpha in [0, 100], defaults to 100
 * @returns RGBA object, or `{ r: null, g: null, b: null, a: null }` if hex is invalid
 */
export const convertHexAndAlphaIntToRgbaObject = (hex, opacity = 100) => {
  return convertHexAndAlphaToRgbaObject(hex, convertAlphaIntToDec(opacity));
};

/**
 * Shifts each RGB channel of a hex color by `brightnessOffset` (positive = lighter, negative = darker),
 * clamped to [0, 255]. This is a flat RGB shift and does not preserve hue or saturation.
 * @param hexColor - Hex color string
 * @param brightnessOffset - Amount to add to each RGB channel; positive lightens, negative darkens
 * @returns Adjusted hex color string, or the original if input is invalid
 */
export function adjustColorBrightness(hexColor, brightnessOffset) {
  if (!hexColor || hexColor.trim() === '') {
    return hexColor;
  }
  const rgbHex = convertHexAndAlphaToRgbaObject(hexColor);
  if (!rgbHex || !isValidRGBA(rgbHex)) {
    return hexColor;
  }
  rgbHex.r = Math.min(255, Math.max(0, rgbHex.r + brightnessOffset));
  rgbHex.g = Math.min(255, Math.max(0, rgbHex.g + brightnessOffset));
  rgbHex.b = Math.min(255, Math.max(0, rgbHex.b + brightnessOffset));
  return convertRgbToHex(rgbHex) || hexColor;
}

// Luma coefficients sourced from https://en.wikipedia.org/wiki/Rec._709#Luma_coefficients
const RED_LUMA_COEFFICIENT = 0.2126;
const GREEN_LUMA_COEFFICIENT = 0.7152;
const BLUE_LUMA_COEFFICIENT = 0.0722;

/**
 * Returns the perceived brightness of a hex color as a luma value in [0, 255],
 * using the Rec. 709 luma coefficients.
 * @param hexColor - Hex color string
 * @returns Luma value in [0, 255], or null if the input is invalid
 */
export const getPerceivedBrightness = hexColor => {
  if (!hexColor || hexColor.trim() === '' || !isValidHex(hexColor)) {
    return null;
  }
  const rgbHex = convertHexAndAlphaToRgbaObject(hexColor);
  if (!rgbHex || !isValidRGBA(rgbHex)) {
    return null;
  }
  const {
    r,
    g,
    b
  } = rgbHex;
  return RED_LUMA_COEFFICIENT * r + GREEN_LUMA_COEFFICIENT * g + BLUE_LUMA_COEFFICIENT * b;
};
const DARK_THRESHOLD = 50;
const LIGHT_THRESHOLD = 128;

/**
 * Returns true if `hexColor` is perceived as dark (luma below `darkThreshold`, default 50).
 * Based on Rec. 709 luma coefficients via `getPerceivedBrightness`.
 * @param hexColor - Hex color string
 * @param darkThreshold - Luma threshold below which a color is considered dark; defaults to 50
 */
export const isDark = (hexColor, darkThreshold = DARK_THRESHOLD) => {
  const perceivedBrightness = getPerceivedBrightness(hexColor);
  return perceivedBrightness !== null && perceivedBrightness < darkThreshold;
};

/**
 * Returns true if `hexColor` is perceived as light (luma above `lightThreshold`, default 128).
 * Based on Rec. 709 luma coefficients via `getPerceivedBrightness`.
 * @param hexColor - Hex color string
 * @param lightThreshold - Luma threshold above which a color is considered light; defaults to 128
 */
export const isLight = (hexColor, lightThreshold = LIGHT_THRESHOLD) => {
  const perceivedBrightness = getPerceivedBrightness(hexColor);
  return perceivedBrightness !== null && perceivedBrightness > lightThreshold;
};

// --- OKLCH color space utilities ---
// OKLCH is a perceptually uniform colorspace (Björn Ottosson's OKLab in polar form).
// Reference: https://bottosson.github.io/posts/oklab/
const srgbToLinear = c => c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
const linearToSrgb = c => c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
function linearRgbToOklab(r, g, b) {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);
  return {
    L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_
  };
}
function oklabToLinearRgb(L, a, b) {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;
  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s
  };
}
function oklabToOklch(L, a, b) {
  const c = Math.sqrt(a * a + b * b);
  let h = Math.atan2(b, a) * 180 / Math.PI;
  if (h < 0) h += 360;
  return {
    l: L,
    c,
    h
  };
}

/**
 * Converts a hex color string to an OKLCH color object.
 * @param hex - Hex color string, e.g. `"#ff0000"`
 * @returns OKLCH object with `l` in [0, 1], `c` in [0, ~0.4], `h` in [0, 360], or null if hex is invalid
 */
export function hexToOklch(hex) {
  if (!isValidHex(hex)) return null;
  const rgb = convertHexAndAlphaToRgbaObject(hex);
  if (rgb.r == null || rgb.g == null || rgb.b == null) return null;
  const rLin = srgbToLinear(rgb.r / 255);
  const gLin = srgbToLinear(rgb.g / 255);
  const bLin = srgbToLinear(rgb.b / 255);
  const {
    L,
    a,
    b
  } = linearRgbToOklab(rLin, gLin, bLin);
  return oklabToOklch(L, a, b);
}

// Slack used when checking if a linear-RGB triple is inside the sRGB gamut.
// Values just outside [0, 1] are treated as in-gamut to avoid rejecting points
// that are merely a floating-point step away from the boundary.
const GAMUT_SLACK = 1e-3;
// Iterations for chroma bisection when finding the gamut-mapped chroma.
const GAMUT_ITERATIONS = 16;
// Conservative upper bound for the chroma binary search — OKLCH chroma rarely
// exceeds ~0.37 for sRGB-representable colors.
const MAX_CHROMA_BOUND = 0.4;
function isInGamut(r, g, b) {
  return r >= -GAMUT_SLACK && r <= 1 + GAMUT_SLACK && g >= -GAMUT_SLACK && g <= 1 + GAMUT_SLACK && b >= -GAMUT_SLACK && b <= 1 + GAMUT_SLACK;
}

/**
 * Returns the largest OKLCH chroma that keeps the color (L, hue) within the
 * sRGB gamut. Hue is passed in radians so callers can share trig across samples.
 * @param l - OKLCH lightness [0, 1]
 * @param hueRad - OKLCH hue in radians
 */
export function maxChromaInGamut(l, hueRad) {
  const cosH = Math.cos(hueRad);
  const sinH = Math.sin(hueRad);
  let lo = 0;
  let hi = MAX_CHROMA_BOUND;
  for (let i = 0; i < GAMUT_ITERATIONS; i++) {
    const mid = (lo + hi) / 2;
    const linear = oklabToLinearRgb(l, mid * cosH, mid * sinH);
    if (isInGamut(linear.r, linear.g, linear.b)) lo = mid;else hi = mid;
  }
  return lo;
}

/**
 * Converts an OKLCH color to a hex color string. If the requested chroma puts
 * the color outside the sRGB gamut, chroma is reduced (L and H preserved) via
 * binary search until the color fits — the CSS Color Level 4 approach.
 * @param l - Lightness in [0, 1]
 * @param c - Chroma (typically 0 to ~0.4)
 * @param h - Hue in degrees [0, 360]
 * @returns Hex color string, or null if conversion fails
 */
export function oklchToHex(l, c, h) {
  const hueRad = h * Math.PI / 180;
  const cosH = Math.cos(hueRad);
  const sinH = Math.sin(hueRad);
  let linear = oklabToLinearRgb(l, c * cosH, c * sinH);
  if (!isInGamut(linear.r, linear.g, linear.b)) {
    let lo = 0;
    let hi = c;
    for (let i = 0; i < GAMUT_ITERATIONS; i++) {
      const mid = (lo + hi) / 2;
      const candidate = oklabToLinearRgb(l, mid * cosH, mid * sinH);
      if (isInGamut(candidate.r, candidate.g, candidate.b)) lo = mid;else hi = mid;
    }
    linear = oklabToLinearRgb(l, lo * cosH, lo * sinH);
  }
  const clamp01 = v => Math.min(1, Math.max(0, v));
  const r8 = Math.round(linearToSrgb(clamp01(linear.r)) * 255);
  const g8 = Math.round(linearToSrgb(clamp01(linear.g)) * 255);
  const b8 = Math.round(linearToSrgb(clamp01(linear.b)) * 255);
  return convertRgbToHex({
    r: r8,
    g: g8,
    b: b8
  });
}

// --- Shade scale algorithm (OKLCH-based, positional) ---
// Positions match the BE ColorShade enum exactly: LIGHTER=0, LIGHT=1, MEDIUM=2, DARK=3, DARKER=4.
// See docs/plans/colorShadeScale.md for the technical source of truth.

// Number of samples used to approximate equal OKLab arc-length placement of
// the stops across [L_FLOOR, L_CEILING]. Higher = more accurate; 40 is plenty.
const ARC_LENGTH_SAMPLES = 40;

/**
 * Tunable constants that drive the shade-scale algorithm. Production uses
 * `DEFAULT_SHADE_SCALE_CONFIG`; the playground drives these via sliders to
 * iterate on output.
 */
/**
 * Valid scale lengths. MVP ships 5 (wired to BE's `ColorShade` enum of
 * LIGHTER / LIGHT / MEDIUM / DARK / DARKER); a future upgrade may bump to 7.
 * Any other value would require a coordinated enum + algorithm change and is
 * intentionally rejected by the type.
 */
export const DEFAULT_SHADE_SCALE_CONFIG = {
  stopCount: 5,
  lFloor: 0.2,
  lCeiling: 0.97,
  chromaTintPower: 0.7,
  chromaShadeRetention: 0.92,
  chromaShadeBlend: 1.5
};

/**
 * Result from {@link getShadeScale}: the stop array plus the index of the
 * anchor stop (the position where the base hex itself appears). Both fields
 * come out of a single algorithm pass so callers don't need to recompute.
 */

/**
 * Returns the anchor index for a color within the shade scale — the stop
 * position where the base color itself appears. All lighter shades sit at
 * lower indices, all darker shades at higher indices.
 *
 * Thin wrapper over {@link getShadeScale}; prefer calling `getShadeScale`
 * directly when you also need the scale array to avoid recomputing it.
 *
 * For the MVP 5-stop scale: 0=LIGHTER, 1=LIGHT, 2=MEDIUM, 3=DARK, 4=DARKER.
 * @param hex - Hex color string
 * @param config - Optional config overrides (defaults to production constants)
 * @returns Anchor index in [0, stopCount-1]; defaults to the middle stop if conversion fails
 */
export function getAnchorPosition(hex, config = DEFAULT_SHADE_SCALE_CONFIG) {
  return getShadeScale(hex, config).anchorIndex;
}

/**
 * Returns the full shade scale for a base hex color. For the MVP 5-stop scale,
 * index 0 = LIGHTER, 1 = LIGHT, 2 = MEDIUM, 3 = DARK, 4 = DARKER. The element
 * at `getAnchorPosition(hex)` is the original `hex` value.
 *
 * Stops are placed at equal OKLab perceptual arc-length between `lFloor` and
 * `lCeiling` so adjacent stops look equally distinct, then the stop nearest
 * the base color's L is snapped to the exact anchor L.
 *
 * Tint-side chroma (tL > ancL) tapers via a power curve on the L headroom.
 * Shade-side chroma (tL < ancL) blends the anchor's absolute chroma with the
 * gamut-relative chroma at the target L; low-saturation anchors get
 * proportionally boosted so darker shades don't render washed out.
 *
 * Hue is preserved exactly across all stops.
 *
 * Returns the scale array plus `anchorIndex` (the position where `hex`
 * appears). Both come out of a single algorithm pass; callers that need both
 * should destructure rather than calling `getAnchorPosition` separately to
 * avoid redundant work.
 *
 * @param hex - Base hex color string
 * @param config - Optional config overrides (defaults to production constants)
 * @returns `{ scale, anchorIndex }` — `scale` has `stopCount` hex strings; `anchorIndex` is in `[0, stopCount-1]`
 */
export function getShadeScale(hex, config = DEFAULT_SHADE_SCALE_CONFIG) {
  const {
    stopCount
  } = config;
  const fallbackIndex = Math.floor((stopCount - 1) / 2);
  if (!hex || !isValidHex(hex)) {
    return {
      scale: Array.from({
        length: stopCount
      }, () => hex),
      anchorIndex: fallbackIndex
    };
  }
  const oklch = hexToOklch(hex);
  if (!oklch) {
    return {
      scale: Array.from({
        length: stopCount
      }, () => hex),
      anchorIndex: fallbackIndex
    };
  }
  const {
    lFloor,
    lCeiling,
    chromaTintPower,
    chromaShadeRetention,
    chromaShadeBlend
  } = config;
  const {
    l: ancL,
    c: ancC,
    h: ancHdeg
  } = oklch;
  const ancH = ancHdeg * Math.PI / 180;
  const cosH = Math.cos(ancH);
  const sinH = Math.sin(ancH);
  const ancMaxC = maxChromaInGamut(ancL, ancH);
  const ancFraction = ancMaxC > 0 ? ancC / ancMaxC : 0;
  const chromaAtL = tL => {
    if (tL > ancL) {
      const ratio = ancL >= lCeiling ? 0 : (1 - tL) / (1 - ancL);
      const clamped = Math.max(0, Math.min(1, ratio));
      return ancC * Math.pow(clamped, chromaTintPower);
    }
    const tMaxC = maxChromaInGamut(tL, ancH);
    const relC = tMaxC * ancFraction;
    const blend = Math.min(1, chromaShadeBlend * (1 - ancFraction));
    return (ancC + (relC - ancC) * blend) * chromaShadeRetention;
  };

  // Sample L uniformly across the range, compute OKLab coordinates for each
  // sample, then integrate arc length. Placing stops at equal arc length yields
  // perceptually even spacing — not just even L, but even apparent distance.
  const sampleLs = Array.from({
    length: ARC_LENGTH_SAMPLES + 1
  }, (_, i) => lFloor + (lCeiling - lFloor) * i / ARC_LENGTH_SAMPLES);
  const sampleAB = sampleLs.map(tL => {
    const tMaxC = maxChromaInGamut(tL, ancH);
    const tC = Math.min(Math.max(0, chromaAtL(tL)), tMaxC);
    return {
      L: tL,
      a: tC * cosH,
      b: tC * sinH
    };
  });
  const cumArc = [0];
  for (let i = 0; i < ARC_LENGTH_SAMPLES; i++) {
    const dL = sampleAB[i + 1].L - sampleAB[i].L;
    const da = sampleAB[i + 1].a - sampleAB[i].a;
    const db = sampleAB[i + 1].b - sampleAB[i].b;
    cumArc.push(cumArc[i] + Math.sqrt(dL * dL + da * da + db * db));
  }
  const arcTotal = cumArc[ARC_LENGTH_SAMPLES];
  const lAtArc = target => {
    if (target <= 0) return sampleLs[0];
    if (target >= arcTotal) return sampleLs[ARC_LENGTH_SAMPLES];
    let lo = 0;
    let hi = ARC_LENGTH_SAMPLES;
    while (lo < hi - 1) {
      const mid = Math.floor((lo + hi) / 2);
      if (cumArc[mid] <= target) lo = mid;else hi = mid;
    }
    const frac = (target - cumArc[lo]) / (cumArc[lo + 1] - cumArc[lo]);
    return sampleLs[lo] + frac * (sampleLs[lo + 1] - sampleLs[lo]);
  };

  // Index 0 = highest L (lightest), last index = lowest L (darkest).
  const Ls = Array.from({
    length: stopCount
  }, (_, i) => lAtArc(arcTotal * (stopCount - 1 - i) / (stopCount - 1)));

  // Snap the stop nearest the anchor to the exact anchor L so the base color
  // appears unchanged at its anchor position.
  let ancIdx = 0;
  let ancBestDist = Infinity;
  for (let i = 0; i < stopCount; i++) {
    const d = Math.abs(Ls[i] - ancL);
    if (d < ancBestDist) {
      ancBestDist = d;
      ancIdx = i;
    }
  }
  Ls[ancIdx] = ancL;
  const scale = Ls.map((tL, i) => {
    if (i === ancIdx) return hex;
    const c = Math.max(0, chromaAtL(tL));
    return oklchToHex(tL, c, ancHdeg) || hex;
  });
  return {
    scale,
    anchorIndex: ancIdx
  };
}

/**
 * Computes the hex color for a single shade position in the scale. Thin
 * wrapper over {@link getShadeScale}: the arc-length placement requires
 * knowing all stops, so per-index queries reuse the whole scale.
 *
 * @param baseHex - The base color hex string
 * @param targetIndex - Position to compute (0 = lightest stop)
 * @param config - Optional config overrides (defaults to production constants)
 * @returns Hex string for the requested shade position, or `baseHex` if inputs are invalid
 */
export function computeShadeHex(baseHex, targetIndex, config = DEFAULT_SHADE_SCALE_CONFIG) {
  if (!baseHex || !isValidHex(baseHex)) return baseHex;
  if (targetIndex < 0 || targetIndex >= config.stopCount) return baseHex;
  return getShadeScale(baseHex, config).scale[targetIndex];
}

/**
 * Semantic token map over a shade scale. Keys mirror the shared `ColorShade`
 * enum / `ColorShadePositions` constant exactly (SCREAMING_CASE), so the same
 * name string that persists in `modifier_value` is the same key consumers read
 * from this map — no conversions.
 *
 * The five primary names (`LIGHTER`, `LIGHT`, `MEDIUM`, `DARK`, `DARKER`) match
 * the 5-stop enum and resolve to the same enum positions at 7 stops. Two
 * additive names (`LIGHTEST`, `DARKEST`) alias `LIGHTER`/`DARKER` at 5 stops
 * and become distinct new extreme positions at 7 stops.
 *
 * Prefer these names over raw `scale[N]` indexing so consumer code stays
 * correct if the scale ever expands from 5 to 7 stops.
 *
 * @param scale - Shade scale array from `getShadeScale`
 * @returns Map of `ColorShade`-enum-aligned token name → hex string
 */
export function getTokens(scale) {
  const n = scale.length;
  const mid = Math.floor((n - 1) / 2);
  return {
    LIGHTEST: scale[0],
    LIGHTER: scale[Math.max(0, mid - 2)],
    LIGHT: scale[Math.max(0, mid - 1)],
    MEDIUM: scale[mid],
    DARK: scale[Math.min(n - 1, mid + 1)],
    DARKER: scale[Math.min(n - 1, mid + 2)],
    DARKEST: scale[n - 1]
  };
}

/**
 * Resolves a shade name (e.g., `"LIGHTER"`, `"DARK"`) to its computed hex color
 * for the given base hex color. Lookup goes through `getTokens` so a future
 * `stopCount` bump (plus coordinated `ColorShadePositions` / `ColorShade` enum
 * change) resolves enum values to the right positions without reshuffling.
 * @param hex - Base hex color string
 * @param shadeName - Shade position name: `"LIGHTER"`, `"LIGHT"`, `"MEDIUM"`, `"DARK"`, or `"DARKER"` (plus `"LIGHTEST"` / `"DARKEST"` at 7 stops)
 * @param config - Optional config overrides (defaults to production constants)
 * @returns Hex string for the resolved shade, or `hex` if `shadeName` is unrecognized
 */
export function resolvePositionalShade(hex, shadeName, config = DEFAULT_SHADE_SCALE_CONFIG) {
  if (!hex || !isValidHex(hex)) return hex;
  const tokens = getTokens(getShadeScale(hex, config).scale);
  if (!(shadeName in tokens)) return hex;
  return tokens[shadeName];
}